import { AxiosResponse } from 'axios'
import {
  AttachmentsApi,
  ConversationApi,
  CustomerApi,
  InboxApi,
  MessagingApi,
} from 'conversations/api'
import { transformResponseIdToString } from 'conversations/utils/stringIdUtil'
import { Message, MessageWithId } from 'shared/src/typings/messages/Message'
import { PaginatedResponse } from 'shared/typings/api/PaginatedResponse'
import { Attachment } from 'shared/typings/conversations/Attachment'
import Conversation from 'shared/typings/conversations/Conversation'
import { QueryWithoutFilterGroups } from 'shared/typings/conversations/query'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { SortOrder } from 'shared/typings/search/SortOrder'
import { Tag, TagPayload } from 'shared/typings/tags/Tag'

import { buildConversationSearchFiltersFromQuery } from '../../utils/ConversationSearchUtil'

interface ConversationSearchResponse {
  hasMore: boolean
  cursor?: string
  conversations: Conversation[]
}

type SubId = string | undefined

export interface SearchFilters {
  inboxIdList?: number[]
  statusList?: string[]
}

interface ConversationSearchRequest {
  cursor?: string
  limit?: number
  sortOrder?: SortOrder
  sortBy?: string
  searchFilters?: SearchFilters
}

export const searchConversations = async (
  query: QueryWithoutFilterGroups,
  subscriptionId: SubId
): Promise<PaginatedResponse<Conversation>> => {
  const searchFilters: SearchFilters = buildConversationSearchFiltersFromQuery(query)
  const url = `/search/subscription/${subscriptionId}`

  const request: ConversationSearchRequest = {
    ...(Object.keys(searchFilters) ? { searchFilters } : {}),
    limit: query.limit,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
    cursor: query.cursor,
  }

  const res = await InboxApi.post<ConversationSearchResponse>(url, request, {
    transformResponse: transformResponseIdToString('endUserId'),
  })

  return {
    data: res.data.conversations,
    pagination: {
      more: res.data.hasMore,
      next: res.data.cursor,
    },
  }
}

export const conversationListCounts = async (
  subscriptionId: string,
  inboxId?: number
): Promise<Record<string, number>> => {
  const url = `/search/subscription/${subscriptionId}/counts`
  const payload = inboxId ? { inboxIdList: [inboxId] } : null
  const res = await InboxApi.post<Record<string, number>>(url, payload)
  return res.data
}

export const inboxBadgeCounts = async (url: string) => {
  const response = await InboxApi.get(url)
  return response.data
}

export const fetchConversation = async (conversationId: number): Promise<Conversation> => {
  const res: AxiosResponse<Conversation> = await ConversationApi.get(
    `/conversations/${conversationId}`,
    {
      transformResponse: transformResponseIdToString('endUserId'),
    }
  )
  return res.data
}

export const fetchTags = async (orgId: number): Promise<Tag[]> => {
  const res = await CustomerApi.get<Tag[]>(`/organizations/${orgId}/tags?onlyEnabled=true`)
  return res.data
}

export const createTag = async (orgId: number, tagPayload: TagPayload): Promise<Tag> => {
  const res = await CustomerApi.post(`/organizations/${orgId}/tags`, tagPayload)
  return res.data
}

export const untagConversation = async (conversationId: number, tagId: number): Promise<void> => {
  const res = await ConversationApi.delete(`/conversations/${conversationId}/tags`, {
    data: [tagId],
  })
  return res.data
}

export const tagConversation = (conversationId: number, tagId: number) =>
  ConversationApi.post(`/conversations/${conversationId}/tags`, [tagId])

export const uploadFile = async (conversationId: number, file: File): Promise<Attachment> => {
  const data = new FormData()
  data.append('file', file)
  data.append('conversationId', `${conversationId}`)

  const res = await AttachmentsApi.post<Attachment>('/attachments', data)
  return res.data
}

export const closeConversation = async (conversationId: number): Promise<void> => {
  ConversationApi.patch(`/conversations/${conversationId}`, { status: 'CLOSED' })
}

export const permacloseConversation = async (conversationId: number): Promise<void> => {
  return ConversationApi.post(`/conversations/${conversationId}/permaclose`)
}

export const updateConversationStatus = async (
  conversationId: number,
  conversationStatus: ConversationStatus
): Promise<void> => {
  ConversationApi.patch(`/conversations/${conversationId}`, { status: conversationStatus })
}

export const fetchLastSyncedTime = async (conversationId: number): Promise<number | undefined> => {
  const resp = await ConversationApi.get<number | undefined>(
    `/conversations/sync/tracking/${conversationId}`
  )
  return resp.data
}

export const createConversation = async (message: Message): Promise<MessageWithId> => {
  const res = await MessagingApi.post<Message, AxiosResponse<MessageWithId>>('/messages', message)
  return res.data
}

export const syncConversation = async (conversationId: number) => {
  return InboxApi.post(`/conversations/${conversationId}/sync`)
}

export const closeAllConversations = async (subscriptionId: string) =>
  InboxApi.post(`/conversations/bulkCloseAdmin/${subscriptionId}`)
